<template>
  <section class="section section--tight src-section">
    <div class="section__inner">
      <div class="part-label">Colophon</div>
      <h2 class="section-h section-h--full">
        Every claim above traces back to <span class="accent">one of these</span>.
      </h2>
      <p class="section-lede section-lede--wide">
        Twenty-five primary and secondary sources. Where two disagreed, the discrepancy is noted
        in the prose. Where the original was a binary PDF — Stroobant's Vervoordt biographical
        résumé; Gerd Fischer's 1999 essay on <em>Kontinuität</em> — it was OCR'd locally and
        quoted directly.
      </p>

      <div v-for="group in groups" :key="group.label" class="src-block">
        <h3 class="src-block__h">{{ group.label }}</h3>
        <ul class="src-list">
          <li v-for="s in group.items" :key="s.id">
            <a :href="s.url" target="_blank" rel="noopener">{{ s.full }}</a>
          </li>
        </ul>
      </div>

      <div class="auth-block">
        <h3 class="src-block__h">Authority IDs · cross-reference</h3>
        <ul class="auth-list">
          <li><a href="https://www.wikidata.org/wiki/Q22236316" target="_blank" rel="noopener">Wikidata <strong>Q22236316</strong></a></li>
          <li><a href="https://viaf.org/viaf/166528644" target="_blank" rel="noopener">VIAF <strong>166528644</strong></a></li>
          <li><a href="https://isni.org/isni/0000000386631606" target="_blank" rel="noopener">ISNI <strong>0000 0003 8663 1606</strong></a></li>
          <li><a href="https://d-nb.info/gnd/143335278" target="_blank" rel="noopener">GND <strong>143335278</strong></a></li>
          <li><a href="https://id.loc.gov/authorities/names/n2016047320" target="_blank" rel="noopener">LoC <strong>n2016047320</strong></a></li>
          <li><a href="https://rkd.nl/artists/75806" target="_blank" rel="noopener">RKD <strong>75806</strong></a></li>
          <li><a href="https://openlibrary.org/authors/OL1180658A" target="_blank" rel="noopener">Open Library <strong>OL1180658A</strong></a></li>
        </ul>
      </div>

      <div class="src-cap">
        <p class="src-cap__name">Dominique Stroobant</p>
        <p class="src-cap__sub">
          b. 16 March 1947, Antwerp · lives in Miseglia, Carrara, since 1970<br />
          Laboratorio STYLITHOS, Via Fantiscritti 11
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SOURCES_REGISTRY } from '../data/media';

const labels: Record<string, string> = {
  wikipedia: 'Wikipedia & encyclopaedias',
  wikidata: 'Wikidata',
  gallery: 'Gallery materials',
  journal: 'Academic articles & press',
  museum: 'Museums & institutions',
  archive: 'Cultural archives',
  blog: 'Specialist blogs and project sites',
  doc: 'Documentaries',
};
const order: string[] = ['wikipedia', 'wikidata', 'gallery', 'journal', 'museum', 'archive', 'blog', 'doc'];

const groups = computed(() => {
  const all = Object.values(SOURCES_REGISTRY);
  return order
    .map((k) => ({ label: labels[k], items: all.filter((s) => s.kind === k) }))
    .filter((g) => g.items.length > 0);
});
</script>

<style scoped>
.src-section { background: var(--paper); }
.src-section :deep(.section-h) {
  font-family: var(--font-body);
  font-weight: 500;
  letter-spacing: 0;
}
.src-section :deep(.section-lede) {
  font-family: var(--font-body);
}
.src-block { margin: 3rem 0; }
.src-block__h {
  font-family: var(--font-body);
  font-style: italic;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0 0 1.2rem;
  letter-spacing: 0;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--vein);
}
.src-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.src-list li {
  padding: 0.7rem 0;
  border-bottom: 1px dotted var(--vein);
  font-size: 1.06rem;
  line-height: 1.5;
}
.src-list li a {
  color: var(--slate);
  text-decoration: underline;
  text-decoration-color: var(--accent-soft);
  text-decoration-thickness: 1.5px;
  text-underline-offset: 3px;
}
.src-list li a:hover { color: var(--accent); }

.auth-block { margin-top: 3rem; }
.auth-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.5rem;
}
.auth-list li {
  font-size: 1rem;
  padding: 0.7rem 0;
}
.auth-list li a {
  color: var(--slate);
  text-decoration: none;
}
.auth-list li a strong { color: var(--accent); font-weight: 600; margin-left: 0.4rem; font-family: var(--font-body); font-size: 1.05em; }

.src-cap {
  margin-top: 5rem;
  padding-top: 3rem;
  border-top: 1px solid var(--vein);
}
.src-cap__name {
  font-family: var(--font-body);
  font-size: clamp(2rem, 3vw, 2.6rem);
  font-weight: 500;
  margin: 0 0 0.4rem;
  color: var(--slate);
  letter-spacing: 0;
}
.src-cap__sub {
  font-size: 1.04rem;
  color: var(--slate-soft);
  line-height: 1.55;
  margin: 0;
}
</style>
